import React, { useCallback, useState } from 'react';
import isNil from 'lodash/isNil';
import { Box, Flex, Stack, xcss } from '@atlaskit/primitives';
import { fg } from '@atlassian/jira-feature-gating';
import {
	FireScreenAnalytics,
	ContextualAnalyticsData,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import type { ImportType } from '../../common/constants/import-type';
import { X2C_SCREENS } from '../../common/constants/metrics';
import type { AdvancedOptions } from '../../common/types';
import { PageDescription, PageHeading } from '../../common/ui';
import { AdvancedOptionsSetting } from '../../common/ui/file-dropzone';
import { StepFooterStandard } from '../../common/ui/step-footer';
import { ProgressBarStandard } from '../../common/ui/step-progress-bar';
import { StepScreen } from '../../common/ui/step-screen';
import {
	useAdvancedOptions,
	useMappingState,
	useUploadedFileName,
} from '../../controllers/import-state';
import { useProductSteps } from '../../controllers/utils';
import { Guide } from './guide';
import { CsvIllustration } from './illustration';
import { UploadConfiguration } from './upload-config';
import { UploadCSV } from './upload-csv';
import { UploadCSVDropZone } from './upload-csv-dropzone';
import { useContent } from './utils';

type UploadScreenProps = {
	onClose: () => void;
	onNext: (importType?: ImportType) => void;
	onCloseAndReturnToUploadStep: () => void;
	onReturn: () => void;
	isCompact?: boolean;
	importType?: ImportType;
};

export const UploadScreenStandard = ({
	onClose,
	onNext,
	onReturn,
	onCloseAndReturnToUploadStep,
	isCompact = true,
	importType,
}: UploadScreenProps) => {
	const { heading, description } = useContent(importType);
	const { productSteps, currentStepIndex } = useProductSteps();
	const [uploadedFileName] = useUploadedFileName();
	const [advancedOptions, { updateAdvancedOptions }] = useAdvancedOptions();
	const [mappingState, { setMappingState }] = useMappingState();
	const [isNextLoading, setIsNextLoading] = useState<boolean>(false);
	const [showCSVFileNotUploadError, setShowCSVFileNotUploadError] = useState<boolean>(false);

	const onChange = useCallback(
		(data: Partial<AdvancedOptions>) => {
			updateAdvancedOptions(data);
			// Reset mapping state if data has previously been loaded
			if (mappingState !== null) {
				setMappingState(null);
			}
		},
		[mappingState, setMappingState, updateAdvancedOptions],
	);

	const onNextClick = useCallback(() => {
		if (!isNil(uploadedFileName)) {
			onNext();
			return;
		}
		setShowCSVFileNotUploadError(true);
	}, [onNext, uploadedFileName]);

	if (fg('enable_x2c_save_configuration')) {
		return (
			<ContextualAnalyticsData sourceName={X2C_SCREENS.UPLOAD_CSV} sourceType={SCREEN}>
				<StepScreen
					renderProgressBar={
						<ProgressBarStandard
							productSteps={productSteps}
							currentStepIndex={currentStepIndex}
							onClose={onCloseAndReturnToUploadStep}
						/>
					}
					renderFooter={
						<StepFooterStandard
							alignRight
							onReturn={onReturn}
							onClickNext={onNextClick}
							isLoading={isNextLoading}
							isFixedPosition
						/>
					}
					maxWidth="1088px"
					testId="project-create-importer-upload-flow.ui.upload-screen-standard"
				>
					<PageHeading>{heading}</PageHeading>
					<Flex gap="space.400">
						<Stack xcss={contentStyles}>
							<PageDescription>{description}</PageDescription>
							<Guide importType={importType} />
							<UploadCSV
								setIsUploading={setIsNextLoading}
								showFileNotUploadedError={showCSVFileNotUploadError}
							/>
							<AdvancedOptionsSetting advancedOptions={advancedOptions} onChange={onChange} />
							<UploadConfiguration setIsUploading={setIsNextLoading} />
						</Stack>
						<Box xcss={illustrationStyles}>
							<CsvIllustration importType={importType} />
						</Box>
					</Flex>
					<FireScreenAnalytics />
				</StepScreen>
			</ContextualAnalyticsData>
		);
	}

	return (
		<ContextualAnalyticsData sourceName={X2C_SCREENS.UPLOAD_CSV} sourceType={SCREEN}>
			<StepScreen
				renderProgressBar={
					!isCompact && (
						<ProgressBarStandard
							productSteps={productSteps}
							currentStepIndex={currentStepIndex}
							onClose={onCloseAndReturnToUploadStep}
						/>
					)
				}
				header={heading}
				description={description}
				renderFooter={
					!isCompact && (
						<StepFooterStandard
							alignRight
							onReturn={onReturn}
							shouldDisableNext={isNil(uploadedFileName)}
							onClickNext={() => onNext()}
							isFixedPosition
						/>
					)
				}
				testId="project-create-importer-upload-flow.ui.upload-screen-standard"
			>
				<UploadCSVDropZone onNext={onNext} onClose={onClose} isCompact={isCompact} />
				<Guide importType={importType} />
				<FireScreenAnalytics />
			</StepScreen>
		</ContextualAnalyticsData>
	);
};

const illustrationStyles = xcss({
	minWidth: '400px',
});

const contentStyles = xcss({
	flexGrow: 1,
});
